import {
  type AuthNextStep,
  type SecondFactorMethod,
  useGetInstanceInfo,
  useGetMe,
  useLoginStaff,
  useSendStaffEmailCode,
  useVerifyStaffTotp,
} from '@postifoto/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { refreshMe } from '../auth/session';
import { Button } from '../components/ui/button';
import { Alert, CodeInput, Field, Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { useErrorMessage } from '../i18n/hooks';
import { AuthLayout, AuthLink } from './auth-layout';

/** Переход после шага входа: код (из письма — уже отправлен), подключение 2FA или в приложение. */
export function useAfterAuthStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return async (next: AuthNextStep) => {
    await refreshMe(queryClient);
    if (next === 'email_code' || next === 'totp') {
      await navigate({
        to: '/login',
        search: { step: 'code', ...(next === 'email_code' ? { sent: true } : {}) },
      });
    } else if (next === 'enroll_totp') await navigate({ to: '/enroll' });
    else await navigate({ to: '/' });
  };
}

export function LoginPage({ step, sent }: { step: 'password' | 'code'; sent: boolean }) {
  return step === 'code' ? <CodeStep sent={sent} /> : <PasswordStep />;
}

function PasswordStep() {
  const t = useTranslations();
  const errorMessage = useErrorMessage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useLoginStaff();
  const after = useAfterAuthStep();
  const navigate = useNavigate();
  const instance = useGetInstanceInfo();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate({ data: { email, password } }, { onSuccess: (result) => after(result.next) });
  };

  return (
    <AuthLayout title={t('login.title')}>
      <form className="grid gap-4" onSubmit={submit}>
        {login.error && <Alert>{errorMessage(login.error)}</Alert>}
        <Field label={t('login.email')}>
          <Input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={t('login.password')}>
          <PasswordInput
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" className="w-full" busy={login.isPending}>
          {t('login.submit')}
        </Button>
        {/* Без настроенной почты ссылку выдаёт только администратор. */}
        {instance.data?.features.email && (
          <AuthLink onClick={() => navigate({ to: '/forgot-password' })}>
            {t('login.forgot')}
          </AuthLink>
        )}
      </form>
    </AuthLayout>
  );
}

type CodeMode = SecondFactorMethod | 'recovery';

/** Отсчёт до повторной отправки кода. */
export function useResendCountdown(initial: number) {
  const [seconds, setSeconds] = useState(initial);
  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setTimeout(() => setSeconds((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);
  return [seconds, setSeconds] as const;
}

/**
 * Второй фактор (ADR-067): по умолчанию — первый способ сессии (обычно код из письма);
 * у кого подключено приложение — переключение на его код и на код восстановления.
 */
function CodeStep({ sent: initiallySent }: { sent: boolean }) {
  const t = useTranslations();
  const errorMessage = useErrorMessage();
  const me = useGetMe();
  const methods = me.data?.mfa.methods ?? [];
  const [mode, setMode] = useState<CodeMode | null>(null);
  const current: CodeMode = mode ?? methods[0] ?? 'totp';
  const [value, setValue] = useState('');
  const [sent, setSent] = useState(initiallySent);
  const [resent, setResent] = useState(false);
  const [wait, setWait] = useResendCountdown(initiallySent ? 30 : 0);
  const verify = useVerifyStaffTotp();
  const send = useSendStaffEmailCode();
  const after = useAfterAuthStep();
  const navigate = useNavigate();

  const switchTo = (next: CodeMode) => {
    setMode(next);
    setValue('');
    verify.reset();
    send.reset();
  };
  const sendCode = () =>
    send.mutate(undefined, {
      onSuccess: (result) => {
        setResent(sent);
        setSent(true);
        setWait(result.resendAfterSeconds);
      },
    });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const data =
      current === 'email'
        ? { emailCode: value }
        : current === 'recovery'
          ? { recoveryCode: value }
          : { code: value };
    verify.mutate({ data }, { onSuccess: (result) => after(result.next) });
  };
  const sessionLost = verify.error && 'status' in verify.error && verify.error.status === 401;
  const email = me.data?.mfa.emailHint ?? '';
  const intro =
    current === 'email'
      ? sent
        ? t('login.emailIntro', { email })
        : t('login.emailNoCode', { email })
      : current === 'totp'
        ? t('login.totpIntro')
        : undefined;
  const needsSend = current === 'email' && !sent;

  return (
    <AuthLayout title={t('login.totpTitle')} intro={intro}>
      <form className="grid gap-4" onSubmit={submit}>
        {verify.error && <Alert>{errorMessage(verify.error)}</Alert>}
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        {resent && current === 'email' && !send.error && (
          <Alert tone="success">{t('login.codeSent')}</Alert>
        )}
        {needsSend ? (
          <Button type="button" className="w-full" busy={send.isPending} onClick={sendCode}>
            {t('login.sendCode')}
          </Button>
        ) : (
          <>
            <Field label={current === 'recovery' ? t('login.recoveryCode') : t('login.code')}>
              {current === 'recovery' ? (
                <Input
                  autoFocus
                  autoComplete="one-time-code"
                  maxLength={16}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              ) : (
                <CodeInput autoFocus value={value} onValueChange={setValue} />
              )}
            </Field>
            <Button
              type="submit"
              className="w-full"
              busy={verify.isPending}
              disabled={current !== 'recovery' && value.length !== 6}
            >
              {t('login.verify')}
            </Button>
          </>
        )}
        {current === 'email' && sent && (
          <AuthLink onClick={sendCode} disabled={wait > 0 || send.isPending}>
            {wait > 0 ? t('login.resendIn', { seconds: wait }) : t('login.resend')}
          </AuthLink>
        )}
        {current !== 'totp' && methods.includes('totp') && (
          <AuthLink onClick={() => switchTo('totp')}>{t('login.useCode')}</AuthLink>
        )}
        {current === 'totp' && (
          <AuthLink onClick={() => switchTo('recovery')}>{t('login.useRecovery')}</AuthLink>
        )}
        {current !== 'email' && methods.includes('email') && (
          <AuthLink
            onClick={() => {
              switchTo('email');
              if (!sent) sendCode();
            }}
          >
            {t('login.useEmail')}
          </AuthLink>
        )}
        {sessionLost && (
          <AuthLink onClick={() => navigate({ to: '/login' })}>{t('login.backToLogin')}</AuthLink>
        )}
      </form>
    </AuthLayout>
  );
}
